import { useState } from 'react'
import { Alert, Box, TextField, Button, Tabs, Tab, CircularProgress } from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { useInvestigationSearch, useInvestigationTraces, useInvestigationOrchestrations } from '../../api/investigation-api'
import { InvestigationSearchResult } from '../../types/Constants'
import DataPanel from './DataPanel'
import ViewerPanel from './ViewerPanel'
import TracesPanel from './TracesPanel'
import OrchestrationPanel from './OrchestrationPanel'

const InvestigationPage = () => {
  const { t } = useTranslation()
  const [searchInput, setSearchInput] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [searchResult, setSearchResult] = useState<InvestigationSearchResult | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [search, isSearching] = useInvestigationSearch()

  const creditApplicationId = searchResult?.creditApplicationId ?? null
  const [tracesResult, isLoadingTraces] = useInvestigationTraces(creditApplicationId)
  const durableInstanceIds = tracesResult?.durableInstanceIds ?? [] as string[]
  const [orchestrations, isLoadingOrchestrations] = useInvestigationOrchestrations(creditApplicationId, durableInstanceIds)

  const handleSearch = async () => {
    const trimmed = searchInput.trim()
    if (!trimmed) return

    setSearchError(null)
    setSearchResult(null)

    const isGuid = trimmed.includes('-') && trimmed.length > 10
    const request = isGuid ? { creditApplicationId: trimmed } : { referenceNumber: parseInt(trimmed, 10) }

    try {
      const result = await search(request)
      setSearchResult(result)
      setActiveTab(0)
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      const message = axiosError.response?.data?.message ?? t('investigation.searchError')
      setSearchError(message)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') void handleSearch()
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('investigation.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ width: 500 }}
        />
        <Button
          variant="contained"
          startIcon={isSearching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          onClick={handleSearch}
          disabled={isSearching || !searchInput.trim()}
        >
          {t('investigation.search')}
        </Button>
      </Box>

      {searchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {searchError}
        </Alert>
      )}

      {searchResult && (
        <>
          <Tabs value={activeTab} onChange={(_, v: number) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab label={t('investigation.tabs.rawData')} />
            <Tab label={t('investigation.tabs.viewer')} />
            <Tab
              label={t('investigation.tabs.traces')}
              icon={isLoadingTraces ? <CircularProgress size={14} /> : undefined}
              iconPosition="end"
            />
            <Tab
              label={t('investigation.tabs.orchestrations')}
              icon={isLoadingOrchestrations ? <CircularProgress size={14} /> : undefined}
              iconPosition="end"
            />
          </Tabs>

          {activeTab === 0 && <DataPanel containers={searchResult.containers} />}
          {activeTab === 1 && <ViewerPanel containers={searchResult.containers} />}
          {activeTab === 2 && <TracesPanel traces={tracesResult?.traces ?? []} isLoading={isLoadingTraces} />}
          {activeTab === 3 && (
            <OrchestrationPanel orchestrations={orchestrations} isLoading={isLoadingOrchestrations} />
          )}
        </>
      )}
    </Box>
  )
}

export default InvestigationPage
